import type { DisplayConversation } from "./ChatPage";
import { preserveNewerQueue } from "./conversationQueueState";
import { preserveConversationTitle } from "../../conversationTitles";

export function applyConversationSnapshot(
  current: DisplayConversation | null,
  incoming: DisplayConversation,
) {
  incoming = preserveNewerQueue(current, incoming);
  return preserveConversationTitle(incoming, current);
}

export function mergeLatestMessages(
  current: DisplayConversation,
  latest: DisplayConversation,
) {
  latest = applyConversationSnapshot(current, latest);
  const currentById = new Map(
    (current.messages ?? []).map((message) => [message.id, message]),
  );
  const latestIds = new Set(
    (latest.messages ?? []).map((message) => message.id),
  );
  const mergedLatest = (latest.messages ?? []).map((message) => {
    const previous = currentById.get(message.id);
    if (!previous) return message;
    // A compact completed snapshot deliberately omits activity; it is not a
    // stale checkpoint. Release the live payload instead of restoring it.
    if (message.activityDeferred) return message;
    const staleText =
      previous.content.length > message.content.length &&
      previous.content.startsWith(message.content);
    const previousReasoning = previous.reasoning ?? "";
    const latestReasoning = message.reasoning ?? "";
    const staleReasoning =
      previousReasoning.length > latestReasoning.length &&
      previousReasoning.startsWith(latestReasoning);
    return {
      ...message,
      ...(staleText
        ? {
            content: previous.content,
            activityEvents: previous.activityEvents,
          }
        : {}),
      ...(staleReasoning
        ? {
            reasoning: previous.reasoning,
            activityEvents: previous.activityEvents,
          }
        : {}),
      subagentRuns: message.subagentRuns?.length
        ? message.subagentRuns
        : previous.subagentRuns,
      generationStatusDetails:
        message.generationStatusDetails ??
        (message.generationStatus === previous.generationStatus
          ? previous.generationStatusDetails
          : undefined),
    };
  });
  const latestUserContents = new Set(
    (latest.messages ?? [])
      .filter((message) => message.role === "user")
      .map((message) => message.content),
  );
  const olderOrLive = (current.messages ?? []).filter(
    (message) =>
      !latestIds.has(message.id) &&
      !(
        message.id.startsWith("optimistic-") &&
        latestUserContents.has(message.content)
      ),
  );
  // `latest` is the server's canonical active-path order. Keeping that order
  // here also repairs a transiently misordered live list before the next
  // refresh, while retaining older paginated messages already on screen.
  const merged = [...olderOrLive, ...mergedLatest];
  return {
    ...latest,
    messages: merged,
    hasMoreMessages: current.hasMoreMessages,
    nextMessageCursor: current.nextMessageCursor,
  };
}
